
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ConsultModal from '../../components/feature/ConsultModal';
import ScrollToTop from '../../components/feature/ScrollToTop';
import LanguageGuard from '../../components/feature/LanguageGuard';
import SeoHead from '../../components/feature/SeoHead';
import { getLangPath, type Language } from '../../router/langRoutes';
import DetailHeroSection from './components/DetailHeroSection';
import WhyLatviaSection from './components/WhyLatviaSection';
import RequirementsSection from './components/RequirementsSection';
import AsianAlleyInvestSection from './components/AsianAlleyInvestSection';
import BenefitsDetailSection from './components/BenefitsDetailSection';
import InvestmentUsageSection from './components/InvestmentUsageSection';
import BlogPreviewSection from './components/BlogPreviewSection';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://thedaeg.com';

const seoData: Record<string, { title: string; description: string; keywords: string }> = {
  vi: {
    title: 'Chi Tiết Định Cư Latvia - Điều Kiện, Quyền Lợi & Đầu Tư Asian Alley | The Daeg',
    description:
      'Thông tin chi tiết về định cư Latvia: Tại sao chọn Latvia, điều kiện đầu tư từ 50.000 EUR, quyền lợi thẻ tạm trú Schengen, đầu tư vào Asian Alley. Tư vấn miễn phí cho người Việt Nam.',
    keywords:
      'chi tiết định cư Latvia, điều kiện định cư Latvia, quyền lợi thẻ tạm trú, đầu tư Asian Alley, định cư châu Âu, thẻ Schengen',
  },
  en: {
    title: 'Latvia Residency Details - Requirements, Benefits & Asian Alley Investment | The Daeg',
    description:
      'Detailed information on Latvia residency: Why choose Latvia, investment from €50,000, Schengen residence permit benefits, invest in Asian Alley supermarket. Free consultation.',
    keywords:
      'Latvia residency details, Latvia investment requirements, Schengen permit benefits, Asian Alley investment, European immigration',
  },
  lv: {
    title: 'Latvijas Uzturēšanās Atļauja - Prasības & Ieguldījumi Asian Alley | The Daeg',
    description:
      'Detalizēta informācija par uzturēšanās atļauju Latvijā: prasības, ieguldījumi no 50 000 EUR, Šengenas atļaujas priekšrocības, ieguldījumi Asian Alley.',
    keywords:
      'Latvijas uzturēšanās atļauja detaļas, Asian Alley ieguldījumi, Šengenas atļauja',
  },
};

const LatviaDetailPage = () => {
  const { i18n } = useTranslation('latvia');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const lang = (i18n.language in seoData ? i18n.language : 'vi') as Language;
  const seo = seoData[lang];

  const investmentSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: seo.title,
    description: seo.description,
    url: `${SITE_URL}${getLangPath('latviaDetail', lang)}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Trang chủ', item: `${SITE_URL}${getLangPath('home', lang)}` },
        { '@type': 'ListItem', position: 2, name: 'Định Cư Latvia', item: `${SITE_URL}${getLangPath('latvia', lang)}` },
        { '@type': 'ListItem', position: 3, name: 'Chi Tiết', item: `${SITE_URL}${getLangPath('latviaDetail', lang)}` },
      ],
    },
  };

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Asian Alley Supermarket Riga',
    description:
      'Chuỗi siêu thị châu Á hàng đầu tại Latvia với 2 cơ sở tại trung tâm thành phố Riga. Chuyên cung cấp thực phẩm và hàng hóa châu Á chất lượng cao.',
    url: SITE_URL,
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Krisjana Barona Iela 96/98',
      addressLocality: 'Riga',
      addressCountry: 'LV',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 56.9496,
      longitude: 24.1052,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday'],
        opens: '09:00',
        closes: '12:00',
      },
    ],
  };

  return (
    <LanguageGuard>
      <div className="min-h-screen bg-white">
        <SeoHead
          title={seo.title}
          description={seo.description}
          keywords={seo.keywords}
          canonicalPath={getLangPath('latviaDetail', lang)}
          schemaJson={[investmentSchema, localBusinessSchema]}
        />
        <header>
          <Navbar onConsultClick={() => setIsModalOpen(true)} />
        </header>

        <main>
          <DetailHeroSection />
          <WhyLatviaSection />
          <RequirementsSection />
          <AsianAlleyInvestSection onConsultClick={() => setIsModalOpen(true)} />
          <BenefitsDetailSection onConsultClick={() => setIsModalOpen(true)} />
          <InvestmentUsageSection />
          <BlogPreviewSection />
        </main>

        <Footer />
        <ConsultModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        <ScrollToTop />
      </div>
    </LanguageGuard>
  );
};

export default LatviaDetailPage;
